import { defineComponent as _defineComponent } from 'vue'
import { getWebmastersSearchGuid } from "@/api/orgstats";
import { patchPaidStatus } from "@/api/payout";
import { IMyLeadgidInvoice, IMyLeadgidInvoiceSummaryItem } from "@/api/types/payout";
import SvgArrow from "@/assets/icons/arrow-down.svg";
import SvgMasterInvoice from "@/assets/icons/master-invoice.svg";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import { USER_ROLES } from "@/mappings/user-roles";
import store from "@/store";
import { ITableFooterButton } from "@/types";
import { ITableHeader, showNotification, showServerError } from "@/utils";
import { CellsType, FormattedItem, prepareItems, RawConfig } from "@/utils/CellsFactory";
import { formatPhone, translateAffiliateStatus } from "@/utils/translate";
import { sortBy } from "lodash-es";
import uniq from "lodash-es/uniq";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";

interface Props {
    items: IMyLeadgidInvoice[];
    tableHeaders?: ITableHeader[];
    loading: boolean;
    limit: number;
    total: number;
    summary?: IMyLeadgidInvoiceSummaryItem[] | Record<string, IMyLeadgidInvoiceSummaryItem>;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppTableInvoices',
  props: {
    items: null,
    tableHeaders: null,
    loading: { type: Boolean },
    limit: null,
    total: null,
    summary: null
  },
  emits: ['update-table'],
  setup(__props: any, { emit }) {

const props = __props as {
    items: IMyLeadgidInvoice[];
    tableHeaders?: ITableHeader[];
    loading: boolean;
    limit: number;
    total: number;
    summary?: IMyLeadgidInvoiceSummaryItem[] | Record<string, IMyLeadgidInvoiceSummaryItem>;
}



const route = useRoute();

const formattedItems = ref<FormattedItem<IMyLeadgidInvoice>[]>([]);
const selected = ref<FormattedItem<IMyLeadgidInvoice>[]>([]);
const webmasters = ref([]);
const isShowSum = ref(false);

const headers = ref<ITableHeader[]>([
    { text: 'ID счёта', value: 'id', align: 'center', width: 136, fixed: true },
    { text: 'Партнёр', value: 'affiliate_id', align: 'center' },
    { text: 'Валюта', value: 'currency', fixed: true },
    { text: 'Доход', value: 'total_revenue', align: 'right' },
    { text: 'Выплата', value: 'total_amount', align: 'right' },
    { text: 'Выплачено', value: 'partials_amount', align: 'right' },
    { text: 'Остаток', value: 'rest_amount', align: 'right' },
    { text: 'Прибыль', value: 'total_profit', align: 'right' },
    { text: 'Способ выплаты', value: 'paid_by_payment_method' },
    { text: 'Реквизиты', value: 'paid_by_requisites' },
    { text: 'Период оплаты', value: 'pay_period', align: 'right' },
    { text: 'Дата создания', value: 'created_at', width: 90, align: 'right' },
    { text: 'Дата обновления', value: 'updated_at', width: 90, align: 'right' },
    { text: 'Статус', value: 'paid_status' },
]);

onBeforeMount(() => {
    if (props.tableHeaders) headers.value = props.tableHeaders;
});

const isSectionHeaders = computed(() => {
    return headers.value.some(h => h.section);
});

const footerButtons: ITableFooterButton[] = [
    {
        text: 'Оплатить',
        textOfConfirm: 'Оплатить?',
        color: 'green',
        icon: 'check',
        action: (): Promise<void> => changePaidStatus('paid'),
    },
    {
        text: 'Отменить оплату',
        textOfConfirm: 'Отменить оплату?',
        color: 'red',
        icon: 'cancel',
        action: (): Promise<void> => changePaidStatus('not_paid'),
    },
];

const changePaidStatus = async (paid_status: 'paid' | 'not_paid'): Promise<void> => {
    try {
        const payload = selected.value.map((item) => ({ invoice_id: item.id.componentData.value, paid_status }));
        await patchPaidStatus({ payload });
        showNotification(paid_status === 'paid' ? `Счета оплачены` : `Оплаты отменены`, 'success');
    } catch (err) {
        showServerError(err, paid_status === 'paid' ? 'Не удалось оплатить' : 'Не удалось отменить оплату');
    } finally {
        selected.value = [];
        emit('update-table');
    }
};

const updateSelected = (newVal: FormattedItem<IMyLeadgidInvoice>[]): void => {
    selected.value = newVal;
};

watch(() => props.items, async () => {
    if (!props.items.length){
        formattedItems.value = [];
        return;
    }
    await getWebmasters();
    formatItems();
});

const cellsConfigs: RawConfig<IMyLeadgidInvoice> = {
    [CellsType.Route]: {
        id: {
            valueKey: 'id',
            name: 'affiliate-invoice',
            isChip: true,
            tooltip: (i: IMyLeadgidInvoice) => i.is_master ? 'Мастер-счёт' : '',
            icon: (i: IMyLeadgidInvoice) => i.is_master ? SvgMasterInvoice : undefined,
            iconIndent: 6,
        },
        affiliate_id: {
            valueKey: 'affiliate_id',
            name: 'affiliate',
            text: (i: IMyLeadgidInvoice) => `${i.affiliate_id} ${i.affiliate || ''}`,
            isChip: (i: IMyLeadgidInvoice) => !i.affiliate,
        },
        master_id: { valueKey: 'master_id', name: 'affiliate', isChip: true },
    },
    [CellsType.Number]: {
        total_revenue: { isMono: true },
        total_amount: { isMono: true },
        partials_amount: { isMono: true },
        total_profit: { isMono: true },
        rest_amount: {
            value: (i: IMyLeadgidInvoice) => {
                const parseNum = (num: number) => num.toLocaleString('ru-RU', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
                return parseNum(i.rest_amount);
            },
            isMono: true,
        },
    },
    [CellsType.Text]: {
        currency: { template: (item: IMyLeadgidInvoice) => item.currency },
        manager_id: { template: (item: IMyLeadgidInvoice) => item.name },
        paid_by_payment_method: { template: (item: IMyLeadgidInvoice) => item.paid_by_payment_method?.name?.ru! },
        current_payment_method: { template: (item: IMyLeadgidInvoice) => item.current_payment_method?.name?.ru! },
        paid_by_requisites: { template: (item: IMyLeadgidInvoice) => setRequisitesData(item, 'paid_by_payment_method') },
        current_requisite: { template: (item: IMyLeadgidInvoice) => setRequisitesData(item, 'current_payment_method') },
    },
    [CellsType.Status]: {
        paid_status: { translate: translateAffiliateStatus },
    },
    [CellsType.Date]: {
        pay_period: {
            isHideTime: true,
        },
        created_at: {},
        updated_at: {},
    },
};

const formatItems = () => {
    const columns = route.query.columns as string[] ?? headers.value.filter(h => !h.fixed).map(h => h.value);
    const fixedCols = headers.value.filter(h => h.fixed).map(h => h.value);
    columns.push(...fixedCols);
    formattedItems.value = prepareItems<IMyLeadgidInvoice>(props.items, cellsConfigs, columns);
};

const getWebmasters = async (): Promise<void> => {
    const dataContract = props.items.filter((i) => i.paid_by_payment_method?.method_id === 'contract_v1');
    if (dataContract.length === 0) return;
    const guids = [...new Set(dataContract.map((j) => j.paid_by_payment_method?.requisite.legal_id))];
    webmasters.value = await getWebmastersSearchGuid(guids as []);
};

const setRequisitesData = (item: IMyLeadgidInvoice, key: 'paid_by_payment_method' | 'current_payment_method') => {
    const prepareRequisite = () => {
        if (item.paid_by_payment_method?.method_id !== 'contract_v1') return item;
        const webmaster = webmasters.value.find(
            (w) => w.guid === item.paid_by_payment_method.requisite.legal_id,
        );
        if (webmaster) {
            item.paid_by_payment_method.requisite.legal_id = webmaster.name;
        }
        return item;
    };

    if (item[key] && item[key].requisite) {
        item = prepareRequisite(item);
        if (item[key]?.method_id === "contract_v1") {
            return item[key].requisite.legal_name;
        } else {
            return Object.entries(item[key].requisite)
                .map(([k, v]) => (k === 'phone' ? formatPhone(v as string) : v))
                .join(', ');
        }
    }

    return '';
};

const isShowSelect = computed(() => {
    const adminRoles = [USER_ROLES.ADMIN, USER_ROLES.SUPERVISOR];
    const user = store.getters['authModule/GET_USER'];
    return adminRoles.some(role => user.roles.includes(role));
});

const getCurrenciesList = computed(() => {
    const defaultOrder = ['RUB', 'KZT', 'USD', 'EUR', 'PLN'];
    //  если тотал запрашиваем с бека, всегда отображаем все валюты(а не только те, которые на данной странице)
    const uniqueCurrencies: string[] = props.summary?.length ? props.summary.map(i => i.currency) : uniq(props.items.map(i => i.currency));
    return sortBy(uniqueCurrencies, (item) => {
        const i = defaultOrder.indexOf(item);
        return i === -1 ? defaultOrder.length : i;
    });
});

const getRows = computed(() => {
    return isShowSum.value ? getCurrenciesList.value : [getCurrenciesList.value[0]];
});

const getDisplayedHeaders = computed(() => {
    const isWithoutColumns = !route.query.columns;
    const displayed = route.query.columns! as string[] || [];
    const fixed = headers.value.filter(h => h.fixed).map(h => h.value);
    const filteredHeaders = headers.value.map(h => h.value);
    if (isWithoutColumns) return filteredHeaders;
    const all = [...displayed, ...fixed];
    return filteredHeaders.filter(h => all.includes(h));
});

const getIndent = (index: number): number => {
    const max = getRows.value.length - 1;
    const rowHeight = 32;
    return (max - index) * rowHeight;
};

const getSum = (head: string, currency: string): number | string => {
    const allowed = ['total_revenue', 'total_amount', 'partials_amount', 'rest_amount', 'total_profit'];
    if (!allowed.includes(head)) return '';

    const isSummaryExist = props.summary && Object.keys(props.summary).length > 0;

    if (isSummaryExist) {
        return getReadySum(head, currency);
    } else {
        return '';
    }
};

const getReadySum = (head: string, currency: string): number | string => {
    const namesFromApi = {
        partials_amount: 'payment_amount',
        rest_amount: 'remain_payment_amount',
    };
    const isArray = Array.isArray(props.summary);
    const value = isArray ?
        props.summary?.find((item) => item.currency === currency)?.[namesFromApi[head] || head] :
        props.summary[currency][namesFromApi[head] || head];
    return value.toLocaleString('ru-RU', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
};

const getPageData = (offset: number) => {
    const params = store.getters['GET_FILTERS_PARAMS_OBJECT'];
    emit('update-table', offset, params);
};

return { __sfc: true,props, emit, route, formattedItems, selected, webmasters, isShowSum, headers, isSectionHeaders, footerButtons, changePaidStatus, updateSelected, cellsConfigs, formatItems, getWebmasters, setRequisitesData, isShowSelect, getCurrenciesList, getRows, getDisplayedHeaders, getIndent, getSum, getReadySum, getPageData, SvgArrow, BaseTable }
}

})